import { Col } from 'antd'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BackgroundBox from '../../../components/custom_components/BackgroundBox'
import PerButton from '../../../components/custom_components/Button'
import { PerEmpty } from '../../../components/custom_components/Empty'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import { LoadingAndErrors } from '../../../components/custom_components/LoadingAndErrors'
import PerText from '../../../components/custom_components/Text'
import { useEmployeeLevelsByCountry } from '../../../graphql/employee/hooks/useEmployeeLevelsByCountry'
import usePyramidValuesAndReferencePyramids from '../../../graphql/pyramids/hooks/usePyramidValuesAndReferencePyramids'
import useUpdateReferencePyramids from '../../../graphql/pyramids/hooks/useUpdateReferencePyramids'
import {
  Country,
  PyramidType,
  ReferencePyramidDetail,
  ReferencePyramidDetailsInput,
  ReferencePyramidInput,
  ReferencePyramidTemplateDto,
  EmployeeLevelEnum,
  PyramidValueDto,
} from '../../../types'
import { formatPyramidReference, formatPyramidsValuesForCompanySettings, formatPyramidValues } from '../../pyramids/pyramidUtils'
import { PyramidsTable } from '../../pyramids/shared/PyramidsTable'
import { CompanySettingsPyramidsEditModal } from './CompanySettingsPyramidsEditModal'
import { CompanySettingsPyramidsGraph } from './CompanySettingsPyramidsGraph'
import { getInitialReferencePyramidData } from './defaultPyramidsUtil'

type Props = {
  referenceTableData: ReferencePyramidDetail[]
  type: PyramidType
  companyId: string
  country: Country
  referencePyramidId: number
  canEdit: boolean
  empty?: boolean
  margin?: boolean
  predefinedScenario?: ReferencePyramidTemplateDto[]
  employeeLevels: EmployeeLevelEnum[]
}

export const CompanySettingsPyramidsRow: FC<Props> = ({
  referenceTableData,
  type,
  companyId,
  country,
  referencePyramidId,
  margin,
  empty,
  predefinedScenario,
  canEdit,
  employeeLevels,
}) => {
  const [t] = useTranslation()
  const colSpanLeft = 8
  const colSpanRight = 16

  const [open, setOpen] = useState(false)
  const { updateReferencePyramids, deleteReferencePyramid } = useUpdateReferencePyramids()

  const { data: employeeLevelsData, error: employeeLevelsError, loading: employeeLevelsLoading } = useEmployeeLevelsByCountry(country)
  const { data, loading, error } = usePyramidValuesAndReferencePyramids(companyId, country, type)

  if (!data || !employeeLevelsData || !data.pyramidValues || !employeeLevelsData.employeeLevels || error || employeeLevelsError) {
    return (
      <LoadingAndErrors
        isLoading={loading || employeeLevelsLoading}
        hasNoData={!data || !data.pyramidValues || !employeeLevelsData || !employeeLevelsData.employeeLevels}
        error={error}
      />
    )
  }

  const showModal = (isOpen: boolean) => {
    setOpen(isOpen)
  }

  const onSave = (value: ReferencePyramidDetail[]) => {
    const details: ReferencePyramidDetailsInput[] = Object.values(value).map((val) => {
      return { range: val.range, limitType: val.limitType, percentage: val.percentage, displayOrder: val.displayOrder }
    })
    const referencePyramidInput: ReferencePyramidInput = {
      id: referencePyramidId,
      companyId: companyId,
      country: country,
      referencePyramidType: type,
      referencePyramidDetailsInput: details,
    }
    updateReferencePyramids(referencePyramidInput)

    showModal(false)
  }

  const onDelete = async (): Promise<void> => {
    const { errors } = await deleteReferencePyramid(referencePyramidId, companyId, country)

    if (!errors) {
      showModal(false)
    }
  }

  const onCancel = (setFormikTableData: (formikTableData: ReferencePyramidDetail[]) => void) => {
    if (!empty) {
      setFormikTableData(referenceTableData)
    } else {
      setFormikTableData(getInitialReferencePyramidData(undefined, type, employeeLevels))
    }
    showModal(false)
  }

  const referenceTableDataForGraph = formatPyramidReference(referenceTableData, country, type, employeeLevels)
  const pyramidValues: PyramidValueDto[] = formatPyramidValues(data.pyramidValues, country, type, employeeLevelsData.employeeLevels)
  const pyramidsData = formatPyramidsValuesForCompanySettings(referenceTableDataForGraph, pyramidValues)

  return (
    <>
      <CompanySettingsPyramidsEditModal
        open={open}
        country={country}
        onCancel={onCancel}
        saveChanges={onSave}
        handleDelete={onDelete}
        type={type}
        initTableData={referenceTableData}
        predefinedScenario={predefinedScenario}
        createNewReference={empty ? empty : false}
        employeeLevels={employeeLevels}
      />

      <PerFlexRow mb={margin ? 'xs' : undefined}>
        <BackgroundBox r color="transparent" noPadding noShadow className="fullWidth">
          {empty ? (
            <BackgroundBox color="gray-1">
              <PerFlexRow justify="center">
                <PerEmpty
                  icon={!canEdit}
                  heading={t('companySettingsView.pyramidsTab.row.empty.heading', { type: type })}
                  info={canEdit ? t('companySettingsView.pyramidsTab.row.empty.info', { type: type }) : undefined}
                  button={
                    canEdit && (
                      <PerButton
                        text={t('companySettingsView.pyramidsTab.row.empty.button', { type: type })}
                        before="add_stroke"
                        color="success"
                        onClick={() => {
                          showModal(true)
                        }}
                      />
                    )
                  }
                />
              </PerFlexRow>
            </BackgroundBox>
          ) : (
            <>
              <Col span={colSpanLeft} className="fullHeight">
                <PerFlexRow direction="column" className="fullHeight">
                  <PyramidsTable
                    tableData={pyramidsData}
                    type={type}
                    variant="companySettings"
                    modalOpen={showModal}
                    className="per-flexRow--fixed"
                    canEdit={canEdit}
                  />
                  <div style={{ marginRight: '1px' }} className="per-flexRow--growShrink">
                    <BackgroundBox mt="xxxs" className="fullHeight" noShadow noPadding />
                  </div>
                </PerFlexRow>
              </Col>
              <Col span={colSpanRight} className="fullHeight">
                <BackgroundBox noShadow height="fullHeight">
                  <PerText appearance="tile-heading" mb="m">
                    {t(`pyramidsView.type.${type}`)}
                  </PerText>
                  <CompanySettingsPyramidsGraph data={pyramidsData} type={type} />
                </BackgroundBox>
              </Col>
            </>
          )}
        </BackgroundBox>
      </PerFlexRow>
    </>
  )
}
